import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ease } from "@/lib/motion";
import { ArrowRight, AlertCircle, CheckCircle2, LucideIcon } from "lucide-react";

export interface SystemShiftItem {
  label: string;
  before: string;
  after: string;
}

export interface SystemShiftProps {
  title?: string;
  label?: string;
  beforeTitle?: string;
  afterTitle?: string;
  items: SystemShiftItem[];
  beforeIcon?: LucideIcon;
  afterIcon?: LucideIcon;
}

const SystemShift = ({
  title = "The structural shift.",
  label = "Transformation",
  beforeTitle = "Before: Fragmented Approach",
  afterTitle = "After: Engineered System",
  items,
  beforeIcon: BeforeIcon = AlertCircle,
  afterIcon: AfterIcon = CheckCircle2,
}: SystemShiftProps) => {
  const [activeView, setActiveView] = useState<"before" | "after">("before");
  const showAfter = activeView === "after";

  const toggleState = () => {
    setActiveView(prev => prev === "before" ? "after" : "before");
  };

  return (
    <section className="py-10 md:py-14 lg:py-20 border-t border-border bg-muted/10">
      <div className="container-wide">
        {/* Header */}
        <motion.div
          className="max-w-2xl mb-8 md:mb-10 lg:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, ease }}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
            {label}
          </span>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">
            {title}
          </h2>
        </motion.div>

        {/* Toggle for Mobile */}
        <div className="lg:hidden mb-8">
          <div className="inline-flex rounded-lg border border-border p-1 bg-background">
            <button
              onClick={() => setActiveView("before")}
              className={`
                px-4 py-2 rounded-md text-sm font-medium transition-all duration-200
                ${activeView === "before" 
                  ? "bg-muted text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
                }
              `}
            >
              Before
            </button>
            <button
              onClick={() => setActiveView("after")}
              className={`
                px-4 py-2 rounded-md text-sm font-medium transition-all duration-200
                ${activeView === "after" 
                  ? "bg-primary text-primary-foreground" 
                  : "text-muted-foreground hover:text-foreground"
                }
              `}
            >
              After
            </button>
          </div>
        </div>

        {/* Mobile: Stacked View */}
        <div className="lg:hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeView}
              initial={{ opacity: 0, x: activeView === "after" ? 20 : -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: activeView === "after" ? -20 : 20 }}
              transition={{ duration: 0.3, ease }}
              className={`
                rounded-xl border p-6
                ${activeView === "before" 
                  ? "border-destructive/20 bg-destructive/5" 
                  : "border-primary/20 bg-primary/5"
                }
              `}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center
                  ${activeView === "before" 
                    ? "bg-destructive/10" 
                    : "bg-primary/10"
                  }
                `}>
                  {activeView === "before" ? (
                    <BeforeIcon className="w-5 h-5 text-destructive" />
                  ) : (
                    <AfterIcon className="w-5 h-5 text-primary" />
                  )}
                </div>
                <h3 className={`font-semibold ${activeView === "before" ? "text-destructive" : "text-primary"}`}>
                  {activeView === "before" ? beforeTitle : afterTitle}
                </h3>
              </div>

              {/* Items */}
              <div className="space-y-4">
                {items.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * index, duration: 0.3 }}
                    className="border-b border-border/50 pb-4 last:border-0 last:pb-0"
                  >
                    <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-1.5">
                      {item.label}
                    </span>
                    <p className="text-foreground">
                      {activeView === "before" ? item.before : item.after}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop: Slider Comparison */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Comparison Container */}
            <div className="grid grid-cols-2 gap-0 relative">
              {/* Before Side */}
              <motion.div
                className="rounded-l-xl border border-r-0 border-destructive/20 bg-destructive/5 p-8 relative overflow-hidden"
                animate={{
                  opacity: showAfter ? 0.5 : 1,
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                    <BeforeIcon className="w-5 h-5 text-destructive" />
                  </div>
                  <h3 className="font-semibold text-destructive">{beforeTitle}</h3>
                </div>

                {/* Items */}
                <div className="space-y-5">
                  {items.map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.05 * index, duration: 0.4 }}
                      className="border-b border-destructive/10 pb-5 last:border-0 last:pb-0"
                    >
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-2">
                        {item.label}
                      </span>
                      <p className="text-foreground">{item.before}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* After Side */}
              <motion.div
                className="rounded-r-xl border border-l-0 border-primary/20 bg-primary/5 p-8 relative overflow-hidden"
                animate={{
                  opacity: showAfter ? 1 : 0.5,
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <AfterIcon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-primary">{afterTitle}</h3>
                </div>

                {/* Items */}
                <div className="space-y-5">
                  {items.map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.05 * index, duration: 0.4 }}
                      className="border-b border-primary/10 pb-5 last:border-0 last:pb-0"
                    >
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-2">
                        {item.label}
                      </span>
                      <p className="text-foreground">{item.after}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Center Divider with Arrow Toggle */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                <motion.button
                  onClick={toggleState}
                  className="w-12 h-12 rounded-full bg-background border-2 border-primary shadow-lg flex items-center justify-center cursor-pointer hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-shadow"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={`Switch to ${showAfter ? "before" : "after"} state`}
                >
                  <motion.div
                    animate={{ rotate: showAfter ? 0 : 180 }}
                    transition={{ duration: 0.3, ease }}
                  >
                    <ArrowRight className="w-5 h-5 text-primary" />
                  </motion.div>
                </motion.button>
              </div>
            </div>

          </div>
        </div>
      </div>

    </section>
  );
};

export default SystemShift;